import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Inbox,
  Mail,
  MessageSquareText,
  Phone,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import {
  deleteContactEnquiry,
  getContactEnquirySummary,
  listContactEnquiries,
  updateContactEnquiryStatus,
} from "./contactEnquiriesApi";
import "./contactEnquiries.css";

const STATUS_META = Object.freeze({
  new: { label: "New", className: "is-new" },
  in_progress: { label: "In Progress", className: "is-progress" },
  resolved: { label: "Resolved", className: "is-resolved" },
});

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function relativeTime(value) {
  if (!value) return "";
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - time) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateTime(value);
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  return <span className={`ce-status ${meta.className}`}>{meta.label}</span>;
}

function SummaryCard({ icon: Icon, label, value, tone, active, onClick }) {
  return (
    <button
      type="button"
      className={`ce-summary-card ${tone} ${active ? "is-active" : ""}`}
      onClick={onClick}
    >
      <span className="ce-summary-icon"><Icon aria-hidden="true" /></span>
      <span>
        <strong>{value ?? 0}</strong>
        <small>{label}</small>
      </span>
      <ChevronRight className="ce-summary-arrow" aria-hidden="true" />
    </button>
  );
}

const ContactEnquiriesPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, new: 0, inProgress: 0, resolved: 0, resolvedRetentionDays: 90 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [status, setStatus] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, limit: 20 });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await getContactEnquirySummary();
      setSummary({
        total: Number(data?.total || 0),
        new: Number(data?.new || 0),
        inProgress: Number(data?.inProgress || 0),
        resolved: Number(data?.resolved || 0),
        resolvedRetentionDays: Number(data?.resolvedRetentionDays || 90),
      });
    } catch (error) {
      console.error("getContactEnquirySummary error:", error);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listContactEnquiries({
        page,
        limit: 20,
        status,
        search: search || undefined,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setMeta({
        total: Number(data?.total || 0),
        totalPages: Math.max(1, Number(data?.totalPages || 1)),
        limit: Number(data?.limit || 20),
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load contact enquiries");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => {
    loadItems();
    loadSummary();
  }, [loadItems, loadSummary]);

  const changeStatusFilter = (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const handleStatusChange = async (item, nextStatus) => {
    if (!item?._id || item.status === nextStatus) return;
    setBusyId(item._id);
    try {
      const data = await updateContactEnquiryStatus(item._id, nextStatus);
      const updated = data?.item;
      toast.success(nextStatus === "resolved" ? "Enquiry resolved" : "Enquiry updated");
      if (selected?._id === item._id && updated) setSelected(updated);
      await Promise.all([loadItems(), loadSummary()]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update enquiry");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleting?._id) return;
    setBusyId(deleting._id);
    try {
      await deleteContactEnquiry(deleting._id);
      toast.success("Enquiry deleted");
      if (selected?._id === deleting._id) setSelected(null);
      setDeleting(null);
      await Promise.all([loadItems(), loadSummary()]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to delete enquiry");
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    if (!selected) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelected(null);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selected]);

  const emptyCopy = useMemo(() => {
    if (search) return "No enquiries match your search.";
    if (status !== "all") return `No ${STATUS_META[status]?.label?.toLowerCase() || status} enquiries.`;
    return "No public website enquiries yet.";
  }, [search, status]);

  return (
    <div className="ce-page">
      <header className="ce-header">
        <div>
          <button type="button" className="ce-back" onClick={() => navigate("/super-admin/venues")}>
            <ArrowLeft aria-hidden="true" /> Back to venues
          </button>
          <div className="ce-title-row">
            <div className="ce-title-icon"><Mail aria-hidden="true" /></div>
            <div>
              <p className="ce-eyebrow"><Sparkles aria-hidden="true" /> Venue Public Website</p>
              <h1>Contact Enquiries</h1>
              <p>Review and manage messages submitted from public venue websites.</p>
            </div>
          </div>
        </div>
        <div className="ce-retention-note">
          <Clock3 aria-hidden="true" />
          <span><strong>{summary.resolvedRetentionDays}-day archive</strong><small>Resolved enquiries become eligible for automatic cleanup after {summary.resolvedRetentionDays} days.</small></span>
        </div>
      </header>

      <section className="ce-summary-grid" aria-label="Enquiry summary">
        <SummaryCard icon={Inbox} label="All enquiries" value={summary.total} tone="is-all" active={status === "all"} onClick={() => changeStatusFilter("all")} />
        <SummaryCard icon={Sparkles} label="New" value={summary.new} tone="is-new" active={status === "new"} onClick={() => changeStatusFilter("new")} />
        <SummaryCard icon={Clock3} label="In progress" value={summary.inProgress} tone="is-progress" active={status === "in_progress"} onClick={() => changeStatusFilter("in_progress")} />
        <SummaryCard icon={CheckCircle2} label="Resolved" value={summary.resolved} tone="is-resolved" active={status === "resolved"} onClick={() => changeStatusFilter("resolved")} />
      </section>

      <section className="ce-toolbar">
        <div className="ce-search">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search venue, customer, phone, email or message…"
            aria-label="Search contact enquiries"
          />
          {searchInput && (
            <button type="button" onClick={() => setSearchInput("")} aria-label="Clear search"><X /></button>
          )}
        </div>
        <div className="ce-filter-pills" aria-label="Status filter">
          {["all", "new", "in_progress", "resolved"].map((value) => (
            <button key={value} type="button" className={status === value ? "is-active" : ""} onClick={() => changeStatusFilter(value)}>
              {value === "all" ? "All" : STATUS_META[value].label}
            </button>
          ))}
        </div>
      </section>

      <section className="ce-list-panel">
        <div className="ce-list-heading">
          <div><strong>{meta.total}</strong> {meta.total === 1 ? "enquiry" : "enquiries"}</div>
          <span>Newest first</span>
        </div>

        {loading ? (
          <div className="ce-loading" role="status"><span className="ce-loader" /> Loading enquiries…</div>
        ) : items.length === 0 ? (
          <div className="ce-empty"><div className="ce-empty-icon"><MessageSquareText /></div><h3>Inbox is clear</h3><p>{emptyCopy}</p></div>
        ) : (
          <div className="ce-card-list">
            {items.map((item, index) => (
              <article className={`ce-card ${item.status === "new" ? "is-unread" : ""}`} key={item._id} style={{ "--ce-delay": `${Math.min(index, 8) * 38}ms` }}>
                <div className="ce-card-top">
                  <div className="ce-venue-tag"><Building2 aria-hidden="true" /><span>{item.venueName || "Venue"}</span><small>PUBLIC WEBSITE</small></div>
                  <div className="ce-card-state"><StatusPill status={item.status} /><time title={formatDateTime(item.createdAt)}>{relativeTime(item.createdAt)}</time></div>
                </div>

                <div className="ce-card-content">
                  <div className="ce-avatar">{String(item.name || "?").trim().charAt(0).toUpperCase()}</div>
                  <div className="ce-card-main">
                    <h2>{item.name}</h2>
                    <div className="ce-contact-line">
                      {item.email && <span><Mail />{item.email}</span>}
                      {item.phone && <span><Phone />{item.phone}</span>}
                    </div>
                    <p className="ce-message-preview">{item.message}</p>
                    {item.preference && <span className="ce-preference">Preference: {item.preference}</span>}
                  </div>
                </div>

                <div className="ce-card-actions">
                  <button type="button" className="ce-btn ce-btn-secondary" onClick={() => setSelected(item)}>View details <ChevronRight /></button>
                  {item.status === "new" && <button type="button" className="ce-btn ce-btn-primary" disabled={busyId === item._id} onClick={() => handleStatusChange(item, "in_progress")}>Mark in progress</button>}
                  {item.status !== "resolved" && item.status !== "new" && <button type="button" className="ce-btn ce-btn-success" disabled={busyId === item._id} onClick={() => handleStatusChange(item, "resolved")}><CheckCircle2 /> Resolve</button>}
                  {item.status === "resolved" && <button type="button" className="ce-btn ce-btn-secondary" disabled={busyId === item._id} onClick={() => handleStatusChange(item, "in_progress")}>Reopen</button>}
                  <button type="button" className="ce-icon-btn is-danger" aria-label={`Delete enquiry from ${item.name}`} onClick={() => setDeleting(item)}><Trash2 /></button>
                </div>
              </article>
            ))}
          </div>
        )}

        {meta.totalPages > 1 && (
          <div className="ce-pagination">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button>
            <span>Page <strong>{page}</strong> of {meta.totalPages}</span>
            <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((value) => Math.min(meta.totalPages, value + 1))}>Next</button>
          </div>
        )}
      </section>

      {selected && (
        <div className="ce-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelected(null); }}>
          <section className="ce-modal" role="dialog" aria-modal="true" aria-labelledby="ce-detail-title">
            <div className="ce-modal-head">
              <div><span className="ce-venue-tag"><Building2 />{selected.venueName}</span><h2 id="ce-detail-title">Enquiry details</h2><p>{formatDateTime(selected.createdAt)}</p></div>
              <button type="button" className="ce-modal-close" onClick={() => setSelected(null)} aria-label="Close enquiry details"><X /></button>
            </div>

            <div className="ce-detail-person">
              <div className="ce-avatar is-large">{String(selected.name || "?").trim().charAt(0).toUpperCase()}</div>
              <div><h3>{selected.name}</h3><StatusPill status={selected.status} /></div>
            </div>

            <div className="ce-detail-grid">
              <div><span><Mail /> Email</span><strong>{selected.email || "Not provided"}</strong></div>
              <div><span><Phone /> Phone</span><strong>{selected.phone || "Not provided"}</strong></div>
              {selected.preference && <div className="is-wide"><span><Sparkles /> Preference</span><strong>{selected.preference}</strong></div>}
              {selected.status === "resolved" && selected.purgeAt && (
                <div className="is-wide">
                  <span><Clock3 /> Automatic cleanup</span>
                  <strong>Eligible after {formatDateTime(selected.purgeAt)}</strong>
                </div>
              )}
            </div>

            <div className="ce-message-box"><span><MessageSquareText /> Message</span><p>{selected.message}</p></div>

            <div className="ce-modal-actions">
              {selected.status === "new" && <button type="button" className="ce-btn ce-btn-primary" disabled={busyId === selected._id} onClick={() => handleStatusChange(selected, "in_progress")}>Mark in progress</button>}
              {selected.status !== "resolved" && <button type="button" className="ce-btn ce-btn-success" disabled={busyId === selected._id} onClick={() => handleStatusChange(selected, "resolved")}><CheckCircle2 /> Resolve</button>}
              {selected.status === "resolved" && <button type="button" className="ce-btn ce-btn-secondary" disabled={busyId === selected._id} onClick={() => handleStatusChange(selected, "in_progress")}>Reopen</button>}
              <button type="button" className="ce-btn ce-btn-danger" onClick={() => { setDeleting(selected); setSelected(null); }}><Trash2 /> Delete</button>
            </div>
          </section>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Delete contact enquiry"
        message={`Delete the enquiry from "${deleting?.name || "this customer"}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={busyId === deleting?._id}
        onConfirm={handleDelete}
        onCancel={() => { if (busyId !== deleting?._id) setDeleting(null); }}
      />
    </div>
  );
};

export default ContactEnquiriesPage;
